import { codeFont, numFont } from '../../utils/fonts';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import InvoiceStatusBadge from '../invoices/InvoiceStatusBadge';
import { formatDate, formatQuantity } from '../../utils';
import { formatPriceWithCurrency } from '../../utils/vat';

const PAGE_SIZE = 25;

/**
 * A customer's parts purchase history across ALL invoices — counter sales,
 * lähetteet and work orders alike. The receipt replacement: warranty and
 * return lookups happen here instead of asking for the paper receipt.
 */
function PurchaseHistoryList({ customerId, onInvoiceClick }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchPage = useCallback(
    async (pageToLoad, replace) => {
      setLoading(true);
      setError('');
      try {
        let url = `/api/customers/${customerId}/purchase-history/?page=${pageToLoad}&page_size=${PAGE_SIZE}`;
        if (debouncedSearch) url += `&search=${encodeURIComponent(debouncedSearch)}`;
        const resp = await axios.get(url);
        const results = resp.data.results || resp.data;
        setCount(resp.data.count ?? results.length);
        setRows((prev) => (replace ? results : [...prev, ...results]));
        setPage(pageToLoad);
      } catch (e) {
        setError(t('Failed to load purchase history'));
      } finally {
        setLoading(false);
      }
    },
    [customerId, debouncedSearch, t]
  );

  useEffect(() => {
    if (customerId) fetchPage(1, true);
  }, [customerId, debouncedSearch, fetchPage]);

  const sourceCaption = (r) =>
    [
      r.vehicle_registration,
      r.source === 'parts_sale' ? t('parts_sales.counter_sale') : null,
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <Box data-testid="purchase-history-list">
      <TextField
        size="small"
        fullWidth
        placeholder={t('Search by part, code or invoice')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" />
            </InputAdornment>
          ),
        }}
        inputProps={{ 'data-testid': 'purchase-history-search' }}
        sx={{ mb: 1.5 }}
      />
      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      {rows.length === 0 && !loading ? (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Inventory2OutlinedIcon sx={{ fontSize: 36, color: 'text.disabled' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {debouncedSearch ? t('No purchases match your search') : t('No purchases yet')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('Date')}</TableCell>
                <TableCell>{t('parts_sales.descriptionCol')}</TableCell>
                <TableCell align="right">{t('parts_sales.qty')}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  {t('parts_sales.total')}{' '}
                  <Typography component="span" variant="caption" color="text.disabled">
                    {t('incl. VAT')}
                  </Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{t('Invoice')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(r.invoice_date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{r.description}</Typography>
                      {r.is_margin_vat_scheme && (
                        <Chip size="small" variant="outlined" color="warning" label={t('parts_sales.marginTag')} sx={{ height: 18, fontSize: 10 }} />
                      )}
                    </Box>
                    {(r.product_code || sourceCaption(r)) && (
                      <Typography variant="caption" color="text.secondary" sx={{ ...codeFont }}>
                        {[r.product_code, sourceCaption(r)].filter(Boolean).join(' · ')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" sx={{ ...numFont, whiteSpace: 'nowrap' }}>
                    {formatQuantity(r.quantity)} {r.unit}
                  </TableCell>
                  <TableCell align="right" sx={{ ...numFont, fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {formatPriceWithCurrency(parseFloat(r.line_total_incl_vat) || 0)}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Link
                        component="button"
                        type="button"
                        variant="body2"
                        onClick={() => onInvoiceClick && onInvoiceClick(r.invoice_id)}
                        sx={{ ...numFont, fontWeight: 600 }}
                      >
                        {r.invoice_number}
                      </Link>
                      <InvoiceStatusBadge status={r.invoice_status} invoice_type={r.invoice_type} size="small" />
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={22} />
        </Box>
      )}
      {!loading && rows.length < count && (
        <Box sx={{ textAlign: 'center', mt: 1.5 }}>
          <Button size="small" onClick={() => fetchPage(page + 1, false)} sx={{ textTransform: 'none' }}>
            {t('Show more')} ({rows.length}/{count})
          </Button>
        </Box>
      )}
    </Box>
  );
}

export default PurchaseHistoryList;
